'use client';

import {
  GetInteractionAggregatesReqFiltersItem,
  GetInteractionAggregatesReqFiltersV2Item,
  CohortFilterGroupChildrenItemOneOf,
  TimeRangeConfig,
} from '@/api/generated/model';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import {
  PARAMS_KEY_AGGREGATE_BY,
  PARAMS_KEY_GLOBAL_FILTERS,
  PARAMS_KEY_KEEP_URL_TIMERANGE,
  PARAMS_KEY_TIME_RANGE,
} from '@/constants/route-params-keys';
import { useQueryParamStateV2 } from '@/utils/use-query-param-state';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { getTimeRangeConfigDefault, getTimeRangeConfigDefaultAsync } from '@/utils/time-range-config';
import { useQuery } from '@tanstack/react-query';
import { useProjectsContext } from '@/providers/projects-provider';
import { QUERY_KEY_DEFAULT_TIMERANGE } from '@/constants/query-keys';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { useAuthContext } from '@/hooks/use-auth';
import { getTimezoneName } from '@/utils/date.util';
import { useLocalStorageState } from '@/hooks/useSessionState';

export type GlobalFiltersContextState = {
  filters: GetInteractionAggregatesReqFiltersItem[];
  setFilters: (filters: GetInteractionAggregatesReqFiltersItem[]) => void;
  timeRange: TimeRangeConfig;
  setTimeRange: (timeRange: TimeRangeConfig) => void;
  aggregateBy: AggregateBy;
  setAggregateBy: (aggregateBy: AggregateBy) => void;
  timeRangeLoading?: boolean;
  setKeepUrlTimerange: (keep: boolean) => void;
  keepUrlTimerange?: boolean;
};

export const GlobalFiltersContext = createContext<GlobalFiltersContextState | null>(null);

const timeRangePersistence = {
  setValue: (projectId: string, value: TimeRangeConfig | undefined) => {
    if (!value) {
      sessionStorage.removeItem(`timeRange-${projectId}`);
      return;
    }

    sessionStorage.setItem(`timeRange-${projectId}`, JSON.stringify(value));
  },
  getValue: (projectId: string): TimeRangeConfig | undefined => {
    const value = sessionStorage.getItem(`timeRange-${projectId}`);
    return value ? (JSON.parse(value) as TimeRangeConfig) : undefined;
  },
};

function isValidAggregateByValue(value: string): value is AggregateBy {
  return value === 'interaction' || value === 'conversation';
}

export const GlobalFiltersProvider = ({ children }: React.PropsWithChildren) => {
  const { defaultAggregation: defaultEnvAggregateBy } = useEnvironmentContext();
  const { selectedProject, defaultAggregateBy } = useProjectsContext();

  const { isSharedLink } = useAuthContext();
  const [keepUrlTimerange, setKeepUrlTimerange] = useQueryParamStateV2(PARAMS_KEY_KEEP_URL_TIMERANGE, false, {
    type: 'objectORnumberORboolean',
  });

  const { data: timerangeFromServer, isFetching: timeRangeLoading } = useQuery(
    [QUERY_KEY_DEFAULT_TIMERANGE, selectedProject?.id],
    () => getTimeRangeConfigDefaultAsync(selectedProject?.id),
    {
      enabled: !!selectedProject?.id,
    },
  );

  const [timeRange, setTimeRange] = useQueryParamStateV2<TimeRangeConfig | undefined>(
    PARAMS_KEY_TIME_RANGE,
    timeRangePersistence.getValue(selectedProject?.id ?? ''),
    {
      type: 'objectORnumberORboolean',
    },
  );

  useEffect(() => {
    if (selectedProject?.id) {
      timeRangePersistence.setValue(selectedProject.id, timeRange);
    }
  }, [selectedProject?.id, timeRange]);

  const { filters, setFilters } = useLocalFilters(PARAMS_KEY_GLOBAL_FILTERS, {
    type: 'filter',
    defaultValue: [],
  });

  const memoizedFilters = useMemo(
    () => filters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)],
  );

  const [aggregateBy, setAggregateBy] = useQueryParamStateV2<AggregateBy>(
    PARAMS_KEY_AGGREGATE_BY,
    // enviroment overrides api default so we don't break self hosted deployments using it
    defaultEnvAggregateBy ?? defaultAggregateBy,
    {
      type: 'string',
    },
  );
  const memoizedAggregateBy = useMemo(
    () => aggregateBy,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(aggregateBy)],
  );

  // ================================================
  // EFFECTS
  // ================================================

  useEffect(() => {
    if (keepUrlTimerange || isSharedLink) return;

    if (timerangeFromServer && !timeRange) {
      setTimeRange(timerangeFromServer);
    }
  }, [selectedProject?.id, isSharedLink, keepUrlTimerange, setTimeRange, timerangeFromServer, timeRange]);

  // safety measure to ensure that aggregateBy is always set
  useEffect(() => {
    if (!isValidAggregateByValue(aggregateBy)) {
      setAggregateBy(defaultEnvAggregateBy || defaultAggregateBy);
    }
  }, [aggregateBy, defaultEnvAggregateBy, defaultAggregateBy, setAggregateBy]);

  const memoizedTimeRange = useMemo(
    () => {
      return { ...(timeRange ?? getTimeRangeConfigDefault()), timezone: getTimezoneName() };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(timeRange)],
  );

  // ================================================
  // JSX
  // ================================================
  const value: GlobalFiltersContextState = useMemo(
    () => ({
      filters: memoizedFilters,
      setFilters,
      timeRange: memoizedTimeRange,
      setTimeRange,
      aggregateBy: memoizedAggregateBy,
      setAggregateBy,
      timeRangeLoading,

      setKeepUrlTimerange,
      keepUrlTimerange,
    }),
    [
      keepUrlTimerange,
      memoizedAggregateBy,
      memoizedFilters,
      memoizedTimeRange,

      setAggregateBy,
      setFilters,
      setKeepUrlTimerange,
      setTimeRange,
      timeRangeLoading,
    ],
  );
  return <GlobalFiltersContext.Provider value={value}>{children}</GlobalFiltersContext.Provider>;
};

export const useGlobalFiltersContext = () => {
  const context = useContext(GlobalFiltersContext);
  if (!context) {
    throw new Error('useGlobalFiltersContext must be used within a GlobalFiltersProvider');
  }

  return context;
};

type AnyFilter = GetInteractionAggregatesReqFiltersV2Item | GetInteractionAggregatesReqFiltersItem;
export const GLOBAL_FILTERS_STORE_KEY = 'globalFilterStore';
export const SHARED_FILTERS_STORE_KEY = 'sharedFilterStore';
export const HIDDEN_FILTERS_STORE_KEY = 'hiddenFilterStore';

export function useGlobalFiltersStore<T extends AnyFilter>() {
  const [filtersStore, setFiltersStore] = useLocalStorageState<Record<string, T[]>>(GLOBAL_FILTERS_STORE_KEY, {});
  return {
    filtersStore,
    setFiltersStore,
  };
}

function useFilterStore<T extends AnyFilter>(
  storeKey: string,
  { selector, defaultValue }: { selector: string; defaultValue: T[] },
) {
  const [filtersStore, setFiltersStore] = useLocalStorageState<Record<string, T[]>>(storeKey, {});

  const value = useMemo(() => filtersStore[selector], [filtersStore, selector]);
  const filters = useMemo(() => value ?? defaultValue, [value, defaultValue]);
  const setFilters = useCallback(
    (filters: T[] | ((filters: T[]) => T[])) => {
      if (typeof filters === 'function') {
        setFiltersStore((prev) => ({ ...prev, [selector]: filters(prev[selector]) }));
      } else {
        setFiltersStore((prev) => ({ ...prev, [selector]: filters }));
      }
    },
    [selector, setFiltersStore],
  );
  return {
    filters,
    setFilters,
  };
}

type UseFilterOptions =
  | {
      type: 'filter';
      defaultValue: GetInteractionAggregatesReqFiltersItem[];
    }
  | {
      type: 'filter_v2';
      defaultValue: GetInteractionAggregatesReqFiltersV2Item[];
    }
  | {
      type: 'user_filter';
      defaultValue: CohortFilterGroupChildrenItemOneOf[];
    }
  | {
      type: 'generic';
      defaultValue: GenericFilter[];
    };

type ExtractFilterType<T extends UseFilterOptions['type']> = T extends 'filter'
  ? GetInteractionAggregatesReqFiltersItem
  : T extends 'filter_v2'
    ? GetInteractionAggregatesReqFiltersV2Item
    : T extends 'user_filter'
      ? CohortFilterGroupChildrenItemOneOf
      : T extends 'generic'
        ? GenericFilter
        : never;

export const useQueryFilters = <T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} => {
  const [filters, setFilters] = useQueryParamStateV2<ExtractFilterType<T>[]>(
    queryKey,
    options.defaultValue as ExtractFilterType<T>[],
    {
      type: 'objectORnumberORboolean',
    },
  );
  const retval = useMemo(() => {
    return { filters, setFilters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), setFilters]);
  return retval;
};

export function useLocalFilters<T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} {
  const { filters, setFilters } = useFilterStore<ExtractFilterType<T>>(GLOBAL_FILTERS_STORE_KEY, {
    selector: queryKey,
    defaultValue: options.defaultValue as ExtractFilterType<T>[],
  });

  const retval = useMemo(() => {
    return {
      filters,
      setFilters,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), setFilters]);

  return retval;
}

function useHiddenFilters<T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} {
  const { filters, setFilters } = useFilterStore<ExtractFilterType<T>>(HIDDEN_FILTERS_STORE_KEY, {
    selector: queryKey,
    defaultValue: options.defaultValue as ExtractFilterType<T>[],
  });

  const retval = useMemo(() => {
    return { filters, setFilters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), setFilters]);

  return retval;
}

export function useSharedFilters<T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} {
  const { filters, setFilters } = useFilterStore<ExtractFilterType<T>>(SHARED_FILTERS_STORE_KEY, {
    selector: queryKey,
    defaultValue: options.defaultValue as ExtractFilterType<T>[],
  });

  const retval = useMemo(() => {
    return { filters, setFilters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), setFilters]);

  return retval;
}

export function useComparedFilters<T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} {
  const { filters: sharedFilters, setFilters: setSharedFilters } = useSharedFilters<T>(queryKey, options);
  const { filters: hiddenFilters, setFilters: setHiddenFilters } = useHiddenFilters<T>(queryKey, options);

  const { filters, setFilters } = useFilterStore<ExtractFilterType<T>>(GLOBAL_FILTERS_STORE_KEY, {
    selector: queryKey,
    defaultValue: options.defaultValue as ExtractFilterType<T>[],
  });

  if (filters.length === 0 && sharedFilters.length > 0) {
    setFilters(sharedFilters);
    setSharedFilters([]);
  } else if (filters.length === 0 && hiddenFilters.length > 0) {
    setFilters(hiddenFilters);
    setHiddenFilters([]);
  }

  const retval = useMemo(() => {
    return { filters, setFilters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), setFilters]);

  return retval;
}

export function useSharedPageFilters<T extends UseFilterOptions['type']>(
  queryKey: string,
  options: Extract<UseFilterOptions, { type: T }>,
): {
  filters: ExtractFilterType<T>[];
  hiddenFilters: ExtractFilterType<T>[];
  setFilters: (filters: ExtractFilterType<T>[] | ((filters: ExtractFilterType<T>[]) => ExtractFilterType<T>[])) => void;
} {
  const { isSharedLink } = useAuthContext();
  const { filters: localFilters, setFilters: setLocalFilters } = useLocalFilters(queryKey, options);
  const { filters: sharedFilters, setFilters: setSharedFilters } = useSharedFilters(queryKey, options);
  const { filters: _hiddenFilters } = useHiddenFilters(queryKey, options);

  const filters = useMemo(() => {
    if (isSharedLink) {
      return sharedFilters;
    }
    return localFilters;
  }, [isSharedLink, localFilters, sharedFilters]);

  const setFilters = useMemo(() => {
    if (isSharedLink) {
      return setSharedFilters;
    }
    return setLocalFilters;
  }, [isSharedLink, setLocalFilters, setSharedFilters]);

  const hiddenFilters = useMemo(() => {
    if (isSharedLink) {
      return _hiddenFilters;
    }
    return [];
  }, [isSharedLink, _hiddenFilters]);

  return { filters, setFilters, hiddenFilters };
}

const SHARED_COLUMNS_STORE_KEY = 'shared-columns-store';

export function useSharedColumns(aggregateBy: AggregateBy) {
  const { isSharedLink } = useAuthContext();

  const searchParams = useMemo(() => {
    return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  }, []);

  const getInitialSharedColumns = useCallback(() => {
    if (!isSharedLink) return null;

    if (aggregateBy === 'interaction') {
      const visibleColumnsParam = searchParams.get('visibleColumns');
      if (visibleColumnsParam) {
        try {
          return JSON.parse(visibleColumnsParam);
        } catch (e) {
          console.error('Error parsing visibleColumns from URL', e);
        }
      }
    } else if (aggregateBy === 'conversation') {
      const visibleConversationColumnsParam = searchParams.get('visibleConversationColumns');
      if (visibleConversationColumnsParam) {
        try {
          return JSON.parse(visibleConversationColumnsParam);
        } catch (e) {
          console.error('Error parsing visibleConversationColumns from URL', e);
        }
      }
    }
    return null;
  }, [isSharedLink, aggregateBy, searchParams]);

  const [sharedColumns, setSharedColumns] = useLocalStorageState<string[] | null>(
    `${SHARED_COLUMNS_STORE_KEY}-${aggregateBy}`,
    null,
  );

  useEffect(() => {
    if (isSharedLink && sharedColumns === null) {
      const initialColumns = getInitialSharedColumns();
      if (initialColumns) {
        setSharedColumns(initialColumns);
      }
    }
  }, [isSharedLink, sharedColumns, getInitialSharedColumns, setSharedColumns]);

  useEffect(() => {
    if (!isSharedLink && sharedColumns !== null) {
      setSharedColumns(null);
    }
  }, [isSharedLink, sharedColumns, setSharedColumns]);

  return {
    sharedColumns: isSharedLink ? sharedColumns : null,
    hasSharedColumnRestrictions: isSharedLink && sharedColumns !== null,
  };
}
